/**
 * Xadrez.
 *
 * As regras vem da chess.js, biblioteca madura e amplamente testada. Regra
 * de xadrez escrita a mao (roque, en passant, promocao, xeque-mate, afogamento,
 * tripla repeticao, regra dos 50 lances) e um viveiro de bug, e aqui cada bug
 * decide para onde vai dinheiro de verdade.
 *
 * O estado guardado e a lista de lances, nao o FEN: repeticao tripla e a regra
 * dos 50 lances dependem do historico, que um FEN sozinho nao carrega.
 *
 * Notacao de lance: SAN ("e4", "Nf3", "O-O", "exd8=Q+").
 */
import { Chess } from 'chess.js';

import { type GameRules, IllegalMoveError, type Outcome, type Seat } from './types.ts';

export interface ChessState {
  /** Lances em SAN, na ordem jogada. */
  readonly moves: readonly string[];
}

/** Reconstroi a posicao replicando o historico. Partidas tem no maximo algumas centenas de lances. */
function replay(state: ChessState): Chess {
  const game = new Chess();
  for (const move of state.moves) {
    game.move(move);
  }
  return game;
}

/** Brancas jogam no assento 0. */
function seatOf(color: 'w' | 'b'): Seat {
  return color === 'w' ? 0 : 1;
}

export const chess: GameRules<ChessState> = {
  id: 'CHESS',

  initial: () => ({ moves: [] }),

  turn: (state) => seatOf(replay(state).turn()),

  legalMoves: (state) => replay(state).moves(),

  apply(state, move) {
    const game = replay(state);
    if (game.isGameOver()) {
      throw new IllegalMoveError(move, 'partida ja terminada');
    }
    try {
      game.move(move);
    } catch {
      // chess.js lanca para lance invalido ou ilegal; o motivo exato nao
      // importa para o cliente, e detalhar ajudaria quem sonda o servidor.
      throw new IllegalMoveError(move, 'lance nao disponivel');
    }
    return { moves: [...state.moves, game.history().at(-1) as string] };
  },

  outcome(state): Outcome | null {
    const game = replay(state);
    if (game.isCheckmate()) {
      // Quem acabou de mover deu o mate; a vez ja passou para o perdedor.
      return {
        kind: 'WIN',
        seat: seatOf(game.turn() === 'w' ? 'b' : 'w'),
        reason: 'XEQUE_MATE',
      };
    }
    if (game.isStalemate()) return { kind: 'DRAW', reason: 'AFOGAMENTO' };
    if (game.isInsufficientMaterial()) return { kind: 'DRAW', reason: 'MATERIAL_INSUFICIENTE' };
    if (game.isThreefoldRepetition()) return { kind: 'DRAW', reason: 'TRIPLA_REPETICAO' };
    if (game.isDraw()) return { kind: 'DRAW', reason: 'REGRA_DOS_50_LANCES' };
    return null;
  },

  serialize: (state) => state.moves.join(' '),

  deserialize(text) {
    const moves = text.trim() === '' ? [] : text.trim().split(/\s+/);
    // Validar na desserializacao evita carregar do banco uma partida
    // corrompida e so descobrir na hora de pagar o premio.
    const game = new Chess();
    for (const move of moves) {
      game.move(move);
    }
    return { moves };
  },
};

/** FEN da posicao, para o cliente desenhar o tabuleiro. */
export function chessFen(state: ChessState): string {
  return replay(state).fen();
}
